import React, { useEffect, useState, useCallback } from "react";
import { api, brl, formatApiErrorDetail, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, Percent, Tag, FileXls, FilePdf, ClockCounterClockwise, CaretDown, CaretRight, Warning,
} from "@phosphor-icons/react";

export default function CommissionsPage() {
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [tables, setTables] = useState([]);
  const [reps, setReps] = useState([]);
  const [history, setHistory] = useState([]);

  const loadAll = useCallback(async () => {
    const [c, r, t, rp] = await Promise.all([
      api.get("/commission/categories"),
      api.get("/commission/rules"),
      api.get("/price-tables"),
      api.get("/representatives"),
    ]);
    setCategories(c.data); setRules(r.data); setTables(t.data); setReps(rp.data);
  }, []);
  useEffect(() => { loadAll().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }, [loadAll]);

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 flex items-center gap-2">
          <Percent size={28} weight="bold" className="text-accent" /> Comissões
        </h1>
        <p className="text-muted-foreground mt-1">Matriz manual: <b>Categoria + Tabela + Prazo + Desconto = Comissão</b>. Acesso exclusivo do administrador.</p>
      </div>

      <Tabs defaultValue="regras" className="w-full">
        <TabsList data-testid="commissions-tabs">
          <TabsTrigger value="regras" data-testid="tab-regras">Regras</TabsTrigger>
          <TabsTrigger value="categorias" data-testid="tab-categorias">Categorias</TabsTrigger>
          <TabsTrigger value="relatorio" data-testid="tab-relatorio">Relatório</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico" onClick={() => api.get("/commission/rules/history").then((r) => setHistory(r.data))}>Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="regras" className="mt-4"><RulesTab rules={rules} categories={categories} tables={tables} reps={reps} reload={loadAll} /></TabsContent>
        <TabsContent value="categorias" className="mt-4"><CategoriesTab categories={categories} reload={loadAll} /></TabsContent>
        <TabsContent value="relatorio" className="mt-4"><ReportTab reps={reps} tables={tables} categories={categories} /></TabsContent>
        <TabsContent value="historico" className="mt-4"><HistoryTab history={history} /></TabsContent>
      </Tabs>
    </div>
  );
}

const emptyRule = { categoria: "", price_table_id: "", prazo: "", desconto_pct: "", comissao_pct: "", representante_id: "none", active: true, observacoes: "" };

function RulesTab({ rules, categories, tables, reps, reload }) {
  const [form, setForm] = useState(emptyRule);
  const [editId, setEditId] = useState(null);
  const selectedTable = tables.find((t) => t.id === form.price_table_id);
  const prazos = selectedTable?.prazos_permitidos || [];

  const save = async () => {
    if (!form.categoria || !form.price_table_id || form.prazo === "" || form.desconto_pct === "" || form.comissao_pct === "") {
      toast.error("Preencha categoria, tabela, prazo, desconto e comissão."); return;
    }
    try {
      const payload = {
        categoria: form.categoria, price_table_id: form.price_table_id, prazo: form.prazo,
        desconto_pct: Number(form.desconto_pct), comissao_pct: Number(form.comissao_pct),
        representante_id: form.representante_id === "none" ? null : form.representante_id,
        active: form.active, observacoes: form.observacoes,
      };
      if (editId) await api.put(`/commission/rules/${editId}`, payload);
      else await api.post("/commission/rules", payload);
      toast.success("Regra salva."); setForm(emptyRule); setEditId(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (r) => { setEditId(r.id); setForm({ categoria: r.categoria, price_table_id: r.price_table_id, prazo: r.prazo, desconto_pct: String(r.desconto_pct), comissao_pct: String(r.comissao_pct), representante_id: r.representante_id || "none", active: r.active, observacoes: r.observacoes || "" }); };
  const remove = async (r) => { if (!window.confirm("Excluir regra?")) return; await api.delete(`/commission/rules/${r.id}`); toast.success("Excluída."); reload(); };

  return (
    <div className="space-y-4">
      <Card className="p-5 shadow-none border-border">
        <Label className="text-sm font-semibold text-zinc-900 mb-3 block">{editId ? "Editar regra" : "Nova regra de comissão"}</Label>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs uppercase text-zinc-500">Categoria</Label>
            <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
              <SelectTrigger data-testid="rule-categoria"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-zinc-500">Tabela</Label>
            <Select value={form.price_table_id} onValueChange={(v) => setForm({ ...form, price_table_id: v, prazo: "" })}>
              <SelectTrigger data-testid="rule-tabela"><SelectValue placeholder="Tabela" /></SelectTrigger>
              <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-zinc-500">Prazo</Label>
            <Select value={form.prazo} onValueChange={(v) => setForm({ ...form, prazo: v })} disabled={!selectedTable}>
              <SelectTrigger data-testid="rule-prazo"><SelectValue placeholder="Prazo" /></SelectTrigger>
              <SelectContent>{prazos.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-zinc-500">Desconto %</Label>
            <Input type="number" step="1" value={form.desconto_pct} onChange={(e) => setForm({ ...form, desconto_pct: e.target.value })} placeholder="0" data-testid="rule-desconto" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-zinc-500">Comissão %</Label>
            <Input type="number" step="0.01" value={form.comissao_pct} onChange={(e) => setForm({ ...form, comissao_pct: e.target.value })} placeholder="0,00" data-testid="rule-comissao" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase text-zinc-500">Representante</Label>
            <Select value={form.representante_id} onValueChange={(v) => setForm({ ...form, representante_id: v })}>
              <SelectTrigger data-testid="rule-rep"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Todos (geral)</SelectItem>
                {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 items-end mt-3">
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs uppercase text-zinc-500">Observações</Label>
            <Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Opcional" data-testid="rule-obs" />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="rule-active" /><span className="text-sm text-zinc-600">Ativa</span></div>
            <Button onClick={save} className="bg-accent hover:bg-accent/90 text-white gap-2 flex-1" data-testid="save-rule-btn"><Plus size={16} weight="bold" /> {editId ? "Atualizar" : "Adicionar"}</Button>
            {editId && <Button variant="ghost" onClick={() => { setEditId(null); setForm(emptyRule); }}>Cancelar</Button>}
          </div>
        </div>
      </Card>

      <Card className="shadow-none border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-zinc-50 border-b border-border">
            {["Categoria", "Tabela", "Prazo", "Desc.", "Comissão", "Representante", "Ativa", "Obs.", ""].map((h, i) => (
              <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 ${[3, 4].includes(i) ? "text-right" : "text-left"}`}>{h}</th>
            ))}
          </tr></thead>
          <tbody className="divide-y divide-border">
            {rules.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Nenhuma regra cadastrada. Adicione acima.</td></tr>}
            {rules.map((r) => (
              <tr key={r.id} className={`hover:bg-zinc-50 ${!r.active ? "opacity-50" : ""}`} data-testid={`rule-row-${r.id}`}>
                <td className="px-4 py-3"><Badge variant="secondary">{r.categoria}</Badge></td>
                <td className="px-4 py-3 text-zinc-700">{r.price_table_nome}</td>
                <td className="px-4 py-3 text-zinc-700">{r.prazo}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.desconto_pct}%</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium text-accent">{r.comissao_pct}%</td>
                <td className="px-4 py-3 text-zinc-600">{r.representante_nome || <span className="text-zinc-400">Todos</span>}</td>
                <td className="px-4 py-3">{r.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Ativa</Badge> : <Badge variant="outline">Inativa</Badge>}</td>
                <td className="px-4 py-3 text-zinc-500 max-w-[160px] truncate" title={r.observacoes}>{r.observacoes || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => edit(r)} className="text-zinc-400 hover:text-accent mr-3" data-testid={`edit-rule-${r.id}`}><PencilSimple size={18} /></button>
                  <button onClick={() => remove(r)} className="text-zinc-400 hover:text-destructive" data-testid={`del-rule-${r.id}`}><Trash size={18} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function CategoriesTab({ categories, reload }) {
  const [nome, setNome] = useState("");
  const add = async () => {
    if (!nome.trim()) return;
    try { await api.post("/commission/categories", { nome, ativo: true }); toast.success("Categoria criada."); setNome(""); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { await api.put(`/commission/categories/${c.id}`, { nome: c.nome, ativo: !c.ativo }); reload(); };
  const remove = async (c) => { if (!window.confirm(`Excluir "${c.nome}"?`)) return; try { await api.delete(`/commission/categories/${c.id}`); toast.success("Excluída."); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="p-5 shadow-none border-border">
        <Label className="text-sm font-semibold text-zinc-900 mb-3 block flex items-center gap-2"><Tag size={16} /> Nova categoria de comissão</Label>
        <div className="flex gap-2">
          <Input value={nome} onChange={(e) => setNome(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ex: Linha Premium" data-testid="new-category-input" />
          <Button onClick={add} className="bg-accent hover:bg-accent/90 text-white gap-2 shrink-0" data-testid="add-category-btn"><Plus size={16} weight="bold" /> Adicionar</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Produtos sem classificação usam a categoria <b>Padrão</b>. Classifique produtos na tela <b>Produtos</b>.</p>
      </Card>
      <Card className="shadow-none border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-zinc-50 border-b border-border">
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Categoria</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Ativa</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {categories.map((c) => (
              <tr key={c.id} className="hover:bg-zinc-50" data-testid={`category-row-${c.nome}`}>
                <td className="px-4 py-3 font-medium text-zinc-800">{c.nome}</td>
                <td className="px-4 py-3"><Switch checked={c.ativo} onCheckedChange={() => toggle(c)} data-testid={`toggle-category-${c.nome}`} /></td>
                <td className="px-4 py-3 text-right">
                  {c.nome !== "Padrão" && <button onClick={() => remove(c)} className="text-zinc-400 hover:text-destructive" data-testid={`del-category-${c.nome}`}><Trash size={18} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ReportTab({ reps, tables, categories }) {
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({ representante_id: "all", cliente_id: "all", price_table_id: "all", categoria: "all", status: "all", start: "", end: "" });
  const [data, setData] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/clients", { params: { limit: 500 } }).then((r) => setClients(r.data)).catch(() => {}); }, []);

  const params = () => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all" && v !== "") p[k] = v; });
    return p;
  };
  const load = async () => {
    setLoading(true);
    try { setData((await api.get("/commission/report", { params: params() })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportFile = (format) => {
    const qs = new URLSearchParams({ format, ...params() }).toString();
    openAuthedFile(`/commission/report/export?${qs}`, `comissoes-dualt.${format === "pdf" ? "pdf" : "xlsx"}`);
  };
  const set = (k) => (v) => setF({ ...f, [k]: v });

  return (
    <div className="space-y-4">
      <Card className="p-5 shadow-none border-border">
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Representante</Label>
            <Select value={f.representante_id} onValueChange={set("representante_id")}><SelectTrigger data-testid="report-rep"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Cliente</Label>
            <Select value={f.cliente_id} onValueChange={set("cliente_id")}><SelectTrigger data-testid="report-cliente"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Tabela</Label>
            <Select value={f.price_table_id} onValueChange={set("price_table_id")}><SelectTrigger data-testid="report-tabela"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{tables.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Categoria</Label>
            <Select value={f.categoria} onValueChange={set("categoria")}><SelectTrigger data-testid="report-categoria"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Status</Label>
            <Select value={f.status} onValueChange={set("status")}><SelectTrigger data-testid="report-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="emitido">Emitido</SelectItem><SelectItem value="faturado">Faturado</SelectItem><SelectItem value="cancelado">Cancelado</SelectItem></SelectContent></Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">De</Label><Input type="date" value={f.start} onChange={(e) => set("start")(e.target.value)} data-testid="report-start" /></div>
          <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Até</Label><Input type="date" value={f.end} onChange={(e) => set("end")(e.target.value)} data-testid="report-end" /></div>
          <Button onClick={load} className="bg-accent hover:bg-accent/90 text-white" data-testid="report-filter-btn">{loading ? "Filtrando..." : "Filtrar"}</Button>
          <Button variant="outline" className="gap-2" onClick={() => exportFile("excel")} data-testid="export-excel-btn"><FileXls size={18} /> Excel</Button>
          <Button variant="outline" className="gap-2" onClick={() => exportFile("pdf")} data-testid="export-pdf-btn"><FilePdf size={18} /> PDF</Button>
        </div>
      </Card>

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="p-5 shadow-none border-border"><p className="text-xs uppercase text-zinc-500">Pedidos</p><p className="font-heading text-2xl font-bold text-zinc-900 tabular-nums">{data.totais.total_pedidos}</p></Card>
            <Card className="p-5 shadow-none border-border"><p className="text-xs uppercase text-zinc-500">Valor líquido</p><p className="font-heading text-2xl font-bold text-zinc-900 tabular-nums">{brl(data.totais.total_vendas)}</p></Card>
            <Card className="p-5 shadow-none border-border bg-accent/5 border-accent/30"><p className="text-xs uppercase text-accent">Comissão total</p><p className="font-heading text-2xl font-bold text-accent tabular-nums" data-testid="report-total-commission">{brl(data.totais.total_comissao)}</p></Card>
          </div>

          {data.grupos.length === 0 && <p className="text-muted-foreground text-center py-8">Nenhum pedido para os filtros.</p>}
          {data.grupos.map((g) => {
            const open = openGroups[g.representante_id];
            return (
              <Card key={g.representante_id} className="shadow-none border-border overflow-hidden" data-testid={`rep-group-${g.representante_id}`}>
                <button onClick={() => setOpenGroups((s) => ({ ...s, [g.representante_id]: !s[g.representante_id] }))} className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 text-left">
                  <div className="flex items-center gap-2">{open ? <CaretDown size={16} /> : <CaretRight size={16} />}<span className="font-semibold text-zinc-900">{g.representante_nome}</span><Badge variant="outline">{g.total_pedidos} pedidos</Badge></div>
                  <div className="flex items-center gap-6 text-sm"><span className="text-zinc-500">Líquido <b className="text-zinc-800 tabular-nums">{brl(g.total_vendas)}</b></span><span className="text-accent font-semibold tabular-nums">{brl(g.total_comissao)}</span></div>
                </button>
                {open && (
                  <div className="border-t border-border divide-y divide-border">
                    {g.pedidos.map((p) => (
                      <div key={p.numero} className="p-4">
                        <div className="flex items-center justify-between mb-2 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-zinc-700">{p.numero}</span>
                            {p.pedido_dualt && <Badge variant="outline">Dualt: {p.pedido_dualt}</Badge>}
                            <span className="text-zinc-500">{p.data}</span>
                            <span className="text-zinc-800">{p.cliente}</span>
                            <Badge variant="secondary">{p.price_table_nome}</Badge>
                            <span className="text-zinc-500">{p.prazo}</span>
                            <Badge variant="outline" className="capitalize">{p.status}</Badge>
                            {p.sem_regra && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-1"><Warning size={12} /> sem regra</Badge>}
                          </div>
                          <span className="text-accent font-semibold tabular-nums">{brl(p.comissao_total)}</span>
                        </div>
                        <table className="w-full text-xs">
                          <thead><tr className="text-zinc-400">
                            {["Produto", "Categoria", "Desc.", "Líquido", "Comissão %", "Comissão"].map((h, i) => (
                              <th key={h} className={`py-1 font-medium ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {p.itens.map((it, i) => (
                              <tr key={`${p.numero}-${it.codigo}-${i}`} className="text-zinc-600">
                                <td className="py-1 pr-2 max-w-[220px] truncate">{it.codigo} {it.descricao}</td>
                                <td className="py-1"><Badge variant="outline" className="text-[10px]">{it.categoria_comissao}</Badge></td>
                                <td className="py-1 text-right tabular-nums">{it.desconto_pct}%</td>
                                <td className="py-1 text-right tabular-nums">{brl(it.valor_liquido)}</td>
                                <td className="py-1 text-right tabular-nums">{it.sem_regra ? <span className="text-amber-600">—</span> : `${it.comissao_pct}%`}</td>
                                <td className="py-1 text-right tabular-nums font-medium text-accent">{brl(it.comissao_valor)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}

function HistoryTab({ history }) {
  return (
    <Card className="shadow-none border-border overflow-x-auto">
      <div className="p-4 border-b border-border flex items-center gap-2 text-sm text-zinc-600"><ClockCounterClockwise size={18} /> Histórico de alterações das regras de comissão</div>
      <table className="w-full text-sm">
        <thead><tr className="bg-zinc-50 border-b border-border">
          {["Quando", "Ação", "Categoria", "Tabela", "Prazo", "Desc.", "Comissão", "Detalhe", "Por"].map((h) => (
            <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">{h}</th>
          ))}
        </tr></thead>
        <tbody className="divide-y divide-border">
          {history.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Sem alterações registradas.</td></tr>}
          {history.map((h) => (
            <tr key={h.id}>
              <td className="px-4 py-2.5 text-zinc-600 whitespace-nowrap">{(h.changed_at || "").replace("T", " ").slice(0, 16)}</td>
              <td className="px-4 py-2.5"><Badge variant="outline" className="capitalize">{h.action}</Badge></td>
              <td className="px-4 py-2.5 text-zinc-800">{h.categoria}</td>
              <td className="px-4 py-2.5 text-zinc-600">{h.price_table_nome || "—"}</td>
              <td className="px-4 py-2.5 text-zinc-600">{h.prazo || "—"}</td>
              <td className="px-4 py-2.5 text-zinc-600">{h.desconto_pct != null ? `${h.desconto_pct}%` : "—"}</td>
              <td className="px-4 py-2.5 text-zinc-600">{h.comissao_pct != null ? `${h.comissao_pct}%` : "—"}</td>
              <td className="px-4 py-2.5 text-zinc-600">{h.detalhe}</td>
              <td className="px-4 py-2.5 text-zinc-600">{h.changed_by || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
